import { Injectable } from '@angular/core';
import OktaAuth, { IDToken, Tokens } from '@okta/okta-auth-js';
import { OktaUser } from 'src/app/shared/models/okta-user';
import { ConfigurationApiService } from './configuration-api.service';
import { clearSessionStorage } from 'src/app/shared/utils/session-storage.utils';
import { checkLocalStorageFlag } from 'src/app/shared/utils/local-storage.utils';
import { Observable, from, mergeMap, of } from 'rxjs';
import { ToastNotificationService } from '../toast-notification.service';
import { isNonEmptyOrWhitespaceString } from 'src/app/shared/utils/string.utils';
import { FeatureFlag } from 'src/app/shared/constants/feature-flags';
import { globalAdminTenantIdUppercase } from 'src/app/shared/constants/constants';

@Injectable({
    providedIn: 'root'
})
export class OktaAuthService {
    private oktaAuthClient!: OktaAuth;

    private cachedUserInfo: OktaUser;

    notAuthMessage = 'Sign-in required.';

    constructor(
        private configApiService: ConfigurationApiService,
        private toastNotificationService: ToastNotificationService
    ) {}

    public initClient(): void {
        const config = this.configApiService.getAuthenticationSettings();
        if (!config.scopes) {
            config.scopes = ['openid', 'profile', 'email', 'offline_access'];
        }
        config.tokenManager = {
            autoRenew: true,
            autoRemove: false
        };
        this.oktaAuthClient = new OktaAuth(config);
        if (checkLocalStorageFlag(FeatureFlag.TokenEventLogging)) {
            this.enableLogging();
        }
    }

    public getClient(): OktaAuth {
        if (!this.oktaAuthClient) {
            this.initClient();
        }

        return this.oktaAuthClient;
    }

    public assertAuthenticatedState(): void {
        const oktaAuthState = this.oktaAuthClient.authStateManager.getAuthState();
        if (!oktaAuthState?.isAuthenticated || !this.oktaAuthClient.getAccessToken()) {
            this.oktaAuthClient.signInWithRedirect();
            console.log(this.notAuthMessage);
            throw new Error(this.notAuthMessage);
        }
    }

    public getUserTenantIdClaimUppercase(): string | null {
        return this.getUserClaimUppercase('tenant_id');
    }

    public isGlobalAdmin(): boolean {
        return this.getUserTenantIdClaimUppercase() === globalAdminTenantIdUppercase;
    }

    public getUserLocationClaimUppercase(): string | null {
        return this.getUserClaimUppercase('location');
    }

    private getUserClaimUppercase(claimKey: string): string | null {
        const state = this.oktaAuthClient.authStateManager.getAuthState();
        if (state === null) {
            return null;
        }

        const claim = state.idToken?.claims?.[claimKey];
        return isNonEmptyOrWhitespaceString(claim) ? (claim as string).toLocaleUpperCase() : null;
    }

    // Returns true if current time is within the early expire window of token expiration or if token does not exist.
    public isAccessTokenExpired(): boolean {
        const oktaAuthState = this.oktaAuthClient.authStateManager.getAuthState();
        const currentExp: number = oktaAuthState?.accessToken?.expiresAt ?? 0;
        const currentExpInMilliseconds = currentExp * 1000;
        const earlyExpireWindowInMilliseconds = 6000;
        return Date.now() > currentExpInMilliseconds - earlyExpireWindowInMilliseconds;
    }

    public async getUser(): Promise<OktaUser> {
        if (this.cachedUserInfo) {
            return { ...this.cachedUserInfo, accessToken: this.getAccessToken() };
        }

        return await this.oktaAuthClient
            .getUser()
            .then(userClaims => {
                const claims = userClaims as Record<string, unknown>;
                const azureUpnClaim = claims['azure_upn'];
                const userInfo = <OktaUser>{
                    fullName: `${userClaims.given_name} ${userClaims.family_name}`,
                    username: userClaims.preferred_username,
                    azureUpn: typeof azureUpnClaim === 'string' ? azureUpnClaim : undefined
                };

                this.cachedUserInfo = userInfo;
                return {
                    ...userInfo,
                    accessToken: this.getAccessToken()
                };
            })
            .catch((reason: any) => {
                this.toastNotificationService.showError('Unable to retrieve user information', 'Unexpected Error');
                throw reason;
            });
    }

    public async signOut(state?: string): Promise<void> {
        const idToken = (await this.oktaAuthClient.tokenManager.get('idToken')) as IDToken;

        if (idToken && idToken.idToken.length > 2000) {
            // Large id tokens cannot use regular signOut due to exceeding URI size
            // Functions require browser allows third-party cookies:
            // https://github.com/okta/okta-auth-js?tab=readme-ov-file#third-party-cookies
            const logoutUrlValue = this.oktaAuthClient.options.postLogoutRedirectUri;
            let logoutUrl: URL;
            if (logoutUrlValue !== undefined) {
                logoutUrl = new URL(logoutUrlValue);
            }

            await this.oktaAuthClient.revokeAccessToken();
            await this.oktaAuthClient.closeSession().then(() => {
                if (logoutUrl) {
                    if (state) {
                        logoutUrl.searchParams.append('state', state);
                    }
                    window.location.href = logoutUrl.toString();
                } else {
                    window.location.reload();
                }
            });
        } else {
            await this.oktaAuthClient.signOut({
                revokeAccessToken: true,
                revokeRefreshToken: true,
                clearTokensBeforeRedirect: true,
                state: state
            });
        }

        clearSessionStorage();
    }

    public renewTokens(): Observable<Tokens> {
        return from(this.oktaAuthClient.token.renewTokens()).pipe(
            mergeMap(renewedTokens => {
                this.oktaAuthClient.tokenManager.setTokens(renewedTokens);
                return of(renewedTokens);
            })
        );
    }

    public getAccessToken(): string {
        return <string>this.oktaAuthClient.getAccessToken();
    }

    private enableLogging(): void {
        // Triggered when a token has expired
        this.oktaAuthClient.tokenManager.on('expired', function (key, expiredToken) {
            console.log('Token with key', key, ' has expired:');
            console.log(expiredToken);
        });
        // Triggered when a token has been renewed
        this.oktaAuthClient.tokenManager.on('renewed', function (key, newToken, oldToken) {
            console.log('Token with key', key, 'has been renewed');
            console.log('Old token:', oldToken);
            console.log('New token:', newToken);
        });
        // Triggered when an OAuthError is returned via the API (typically during token renew)
        this.oktaAuthClient.tokenManager.on('error', function (err) {
            console.log('TokenManager error:', err);
            // err.name
            // err.message
            // err.errorCode
            // err.errorSummary
            // err.tokenKey
            // err.accessToken
        });
    }
}
